import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateBranchThunk,
  getBranchesThunk,
} from "../../../store/thunks/branchThunks";
import WorkingHoursConfig from "../config/WorkingHoursConfig";
import ApprovalMatrixConfig from "../config/ApprovalMatrixConfig";
import BranchFormTabs from "./common/BranchFormTabs";
import BranchBasicInfo from "./common/BranchBasicInfo";
import BranchAddressInfo from "./common/BranchAddressInfo";
import BranchContactInfo from "./common/BranchContactInfo";
import BranchRateConfig from "./common/BranchRateConfig";
import BranchSettingsInfo from "./common/BranchSettingsInfo";
import BranchComplianceInfo from "./common/BranchComplianceInfo";
import BranchFinancialSection from "./common/BranchFinancialSection";

const EditBranchForm = ({ branch, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { branches } = useSelector((state) => state.branch);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const [formData, setFormData] = useState({
    // Basic Information
    name: branch.name || "",
    code: branch.code || "",
    branchType: branch.branchType || "STANDARD",
    cityTier: branch.cityTier || "TIER2",
    parentBranchId: branch.parentBranchId || "",

    // Address
    address: {
      line1: branch.address?.line1 || "",
      line2: branch.address?.line2 || "",
      city: branch.address?.city || "",
      state: branch.address?.state || "",
      pinCode: branch.address?.pinCode || "",
      landmark: branch.address?.landmark || "",
      latitude: branch.address?.latitude || "",
      longitude: branch.address?.longitude || "",
      googleMapsUrl: branch.address?.googleMapsUrl || "",
    },

    // Contact
    contact: {
      phoneNumber: branch.contact?.phoneNumber || "",
      alternatePhone: branch.contact?.alternatePhone || "",
      whatsappNumber: branch.contact?.whatsappNumber || "",
      email: branch.contact?.email || "",
      managerName: branch.contact?.managerName || "",
      managerPhone: branch.contact?.managerPhone || "",
      managerEmail: branch.contact?.managerEmail || "",
    },

    // Rate Configuration
    rateConfig: {
      ratePolicy: branch.rateConfig?.ratePolicy || "INHERIT",
      rateMode: branch.rateConfig?.rateMode || "ADMIN",
      inheritFromBranch: branch.rateConfig?.inheritFromBranch || "",
      autoSync: {
        enabled: branch.rateConfig?.autoSync?.enabled || false,
        frequency: branch.rateConfig?.autoSync?.frequency || "DAILY",
      },
      adjustments: {
        transport: branch.rateConfig?.adjustments?.transport || 0,
        operational: branch.rateConfig?.adjustments?.operational || 0,
        rent: branch.rateConfig?.adjustments?.rent || 0,
        margin: branch.rateConfig?.adjustments?.margin || 0,
      },
      marginOverrides: {
        gold: {
          sellMargin: branch.rateConfig?.marginOverrides?.gold?.sellMargin || 0,
          buyMargin: branch.rateConfig?.marginOverrides?.gold?.buyMargin || 0,
        },
        silver: {
          sellMargin:
            branch.rateConfig?.marginOverrides?.silver?.sellMargin || 0,
          buyMargin: branch.rateConfig?.marginOverrides?.silver?.buyMargin || 0,
        },
        platinum: {
          sellMargin:
            branch.rateConfig?.marginOverrides?.platinum?.sellMargin || 0,
          buyMargin:
            branch.rateConfig?.marginOverrides?.platinum?.buyMargin || 0,
        },
        diamond: {
          margin: branch.rateConfig?.marginOverrides?.diamond?.margin || 0,
        },
      },
    },

    // Settings
    settings: {
      // Transaction Settings
      allowCrossBranchRedemption:
        branch.settings?.allowCrossBranchRedemption || false,
      allowInterBranchTransfer:
        branch.settings?.allowInterBranchTransfer || true,

      // Approval Settings
      requiresApprovalForHighValue:
        branch.settings?.requiresApprovalForHighValue || true,
      highValueThreshold: branch.settings?.highValueThreshold || 50000,

      // Discount Settings
      maxDiscountPercent: branch.settings?.maxDiscountPercent || 10,
      discountApprovalRequired:
        branch.settings?.discountApprovalRequired || true,
      discountApprovalThreshold:
        branch.settings?.discountApprovalThreshold || 5,

      // Operational Settings
      allowRateOverride: branch.settings?.allowRateOverride || false,

      // Working Hours
      workingHours: branch.settings?.workingHours || {},
      holidays: branch.settings?.holidays || [],

      // Approval Matrix
      approvalMatrix: branch.settings?.approvalMatrix || [],
    },

    // Compliance
    compliance: {
      gstNumber: branch.compliance?.gstNumber || "",
      panNumber: branch.compliance?.panNumber || "",
      tradeLicense: branch.compliance?.tradeLicense || "",
      establishmentYear:
        branch.compliance?.establishmentYear || new Date().getFullYear(),
      hallmarkingLicense: branch.compliance?.hallmarkingLicense || "",
      bisRegistration: branch.compliance?.bisRegistration || "",
    },

    // Financial
    financial: {
      bankAccount: {
        accountName: branch.financial?.bankAccount?.accountName || "",
        accountNumber: branch.financial?.bankAccount?.accountNumber || "",
        bankName: branch.financial?.bankAccount?.bankName || "",
        branch: branch.financial?.bankAccount?.branch || "",
        ifscCode: branch.financial?.bankAccount?.ifscCode || "",
      },
      cashLimit: branch.financial?.cashLimit || 200000,
      transactionLimit: {
        daily: branch.financial?.transactionLimit?.daily || "",
        monthly: branch.financial?.transactionLimit?.monthly || "",
      },
    },
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(getBranchesThunk({ isActive: true }));
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = "Branch name is required";
    if (!formData.address.line1) newErrors.line1 = "Address is required";
    if (!formData.address.city) newErrors.city = "City is required";
    if (!formData.address.state) newErrors.state = "State is required";
    if (!formData.address.pinCode) newErrors.pinCode = "PIN code is required";
    if (!formData.contact.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";

    // Validate phone number format
    if (
      formData.contact.phoneNumber &&
      !/^[6-9]\d{9}$/.test(formData.contact.phoneNumber)
    ) {
      newErrors.phoneNumber =
        "Invalid phone number (10 digits starting with 6-9)";
    }

    // Validate PIN code
    if (
      formData.address.pinCode &&
      !/^[1-9][0-9]{5}$/.test(formData.address.pinCode)
    ) {
      newErrors.pinCode = "Invalid PIN code (6 digits)";
    }

    // Validate GST if provided
    if (
      formData.compliance.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.compliance.gstNumber
      )
    ) {
      newErrors.gstNumber = "Invalid GST number format";
    }

    // Validate IFSC if provided
    if (
      formData.financial.bankAccount.ifscCode &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.financial.bankAccount.ifscCode)
    ) {
      newErrors.ifscCode = "Invalid IFSC code format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add the same helper function before handleSubmit
  const cleanFormData = (data) => {
    const cleaned = { ...data };

    // Remove empty string values for ObjectId fields
    if (!cleaned.parentBranchId) {
      delete cleaned.parentBranchId;
    }

    if (!cleaned.rateConfig?.inheritFromBranch) {
      delete cleaned.rateConfig.inheritFromBranch;
    }

    // Clean up empty strings in nested objects
    if (cleaned.financial?.transactionLimit) {
      if (!cleaned.financial.transactionLimit.daily) {
        delete cleaned.financial.transactionLimit.daily;
      }
      if (!cleaned.financial.transactionLimit.monthly) {
        delete cleaned.financial.transactionLimit.monthly;
      }
    }

    return cleaned;
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const errorFields = Object.keys(errors);
      if (
        errorFields.some((field) =>
          ["name", "branchType", "cityTier"].includes(field)
        )
      ) {
        setActiveTab("basic");
      } else if (
        errorFields.some(
          (field) =>
            field.startsWith("line") ||
            field.includes("city") ||
            field.includes("state") ||
            field.includes("pinCode")
        )
      ) {
        setActiveTab("address");
      } else if (errorFields.some((field) => field.includes("phone"))) {
        setActiveTab("contact");
      } else if (errorFields.some((field) => field.includes("gst"))) {
        setActiveTab("compliance");
      } else if (errorFields.some((field) => field.includes("ifsc"))) {
        setActiveTab("financial");
      }
      return;
    }

    setLoading(true);
    try {
      // Clean the form data before sending
      const cleanedData = cleanFormData(formData);
      await dispatch(
        updateBranchThunk({
          branchId: branch._id,
          branchData: cleanedData,
        })
      ).unwrap();

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      console.error("Failed to update branch:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const updateNestedField = (section, subsection, field, value) => {
    if (subsection) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            [field]: value,
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    }
  };

  const tabs = [
    "basic",
    "address",
    "contact",
    "rates",
    "settings",
    "compliance",
    "financial",
    "hours",
  ];

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg">
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-bold">Edit Branch - {branch.name}</h2>
        <BranchFormTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {/* Basic Information Tab */}
        {activeTab === "basic" && (
          <BranchBasicInfo
            formData={formData}
            updateField={updateField}
            errors={errors}
            branches={branches}
            isEdit={true}
            isMainBranch={branch.isMainBranch}
          />
        )}

        {/* Address Tab */}
        {activeTab === "address" && (
          <BranchAddressInfo
            formData={formData}
            updateField={updateField}
            errors={errors}
          />
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <BranchContactInfo
            formData={formData}
            updateField={updateField}
            errors={errors}
          />
        )}

        {/* Rate Configuration Tab */}
        {activeTab === "rates" && (
          <BranchRateConfig
            formData={formData}
            updateField={updateField}
            updateNestedField={updateNestedField}
            branches={branches}
          />
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <BranchSettingsInfo
            formData={formData}
            updateField={updateField}
            approvalMatrix={formData.settings.approvalMatrix}
            onApprovalMatrixChange={(matrix) =>
              updateField("settings", "approvalMatrix", matrix)
            }
          />
        )}

        {/* Compliance Tab */}
        {activeTab === "compliance" && (
          <BranchComplianceInfo
            formData={formData}
            updateField={updateField}
            errors={errors}
          />
        )}

        {/* Financial Tab */}
        {activeTab === "financial" && (
          <BranchFinancialSection
            formData={formData}
            updateField={updateField}
            updateNestedField={updateNestedField}
            errors={errors}
          />
        )}

        {/* Working Hours Tab */}
        {activeTab === "hours" && (
          <WorkingHoursConfig
            workingHours={formData.settings.workingHours}
            holidays={formData.settings.holidays}
            onChange={(data) => {
              updateField("settings", "workingHours", data.workingHours);
              updateField("settings", "holidays", data.holidays);
            }}
          />
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBranchForm;
